/** Shared empty-state copy for dashboard section cards. */
export const DASHBOARD_SECTION_EMPTY_TITLE = 'No data to display'
export const DASHBOARD_SECTION_EMPTY_DESCRIPTION =
  "There's no data to show here"

/** True once a section has finished loading and still has nothing to render. */
export function shouldShowDashboardSectionEmpty(
  loading: boolean,
  hasData: boolean,
): boolean {
  return !loading && !hasData
}

/**
 * True while a section is fetching and has no payload yet.
 * Background refresh keeps prior data visible (`hasPayload` stays true).
 */
export function isDashboardSectionPending(
  loading: boolean,
  hasPayload: boolean,
): boolean {
  return loading && !hasPayload
}

/**
 * Parse a dashboard metric into a finite number for empty-state checks.
 * Accepts raw numbers and common display formats (currency, %, hrs/mins, K/M/B).
 */
function toDashboardNumericValue(
  value: number | string | null | undefined,
): number | null {
  if (value == null) return null
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }

  const trimmed = value.trim()
  if (!trimmed || trimmed === '-' || trimmed === '—') return null

  // Compound durations ("1 hr 30 mins"): sum hour/minute tokens into minutes.
  const durationToken = /(-?[\d.]+)\s*(hrs?|hr|h|mins?|min)\b/gi
  const durationMatches = Array.from(trimmed.matchAll(durationToken))
  if (durationMatches.length > 1) {
    let totalMinutes = 0
    for (const match of durationMatches) {
      const amount = Number(match[1])
      if (!Number.isFinite(amount)) return null
      const unit = match[2].toLowerCase()
      totalMinutes += unit.startsWith('h') ? amount * 60 : amount
    }
    return totalMinutes
  }

  let normalized = trimmed
    .replace(/,/g, '')
    .replace(/\$/g, '')
    .replace(/%/g, '')
    .trim()

  const compactMatch = normalized.match(/^(-?[\d.]+)\s*([KMB])$/i)
  if (compactMatch) {
    const amount = Number(compactMatch[1])
    if (!Number.isFinite(amount)) return null
    const suffix = compactMatch[2].toUpperCase()
    if (suffix === 'K') return amount * 1_000
    if (suffix === 'M') return amount * 1_000_000
    return amount * 1_000_000_000
  }

  normalized = normalized
    .replace(/\s*(hrs?|hr|h|mins?|min|days?|d)\s*$/i, '')
    .trim()
  if (!normalized) return null

  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * True when any value is a finite number unequal to zero.
 * All-zero / empty / invalid values do not count as dashboard activity.
 */
export function hasNonZeroDashboardValues(
  ...values: Array<number | string | null | undefined>
): boolean {
  return values.some((value) => {
    const parsed = toDashboardNumericValue(value)
    return parsed != null && parsed !== 0
  })
}
